import React, { useEffect, useState } from "react";
import { Dialog } from "@headlessui/react";
import { LogIn, ShieldAlert } from "lucide-react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout } from "../../store/authSlice";
import {
    resetSessionExpiredPrompt,
    SESSION_EXPIRED_EVENT,
} from "../../utils/sessionExpiredEvents";

const fallbackDetails = {
    loginPath: "/login",
    message: "Your sign-in session has expired after the allowed time. Please sign in again to continue working securely.",
};

const SessionExpiredModal = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [details, setDetails] = useState(null);

    useEffect(() => {
        const handleSessionExpired = (event) => {
            setDetails({
                ...fallbackDetails,
                ...(event.detail || {}),
            });
        };

        window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
        return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    }, []);

    const handleBackToSignIn = () => {
        const loginPath = details?.loginPath || fallbackDetails.loginPath;
        dispatch(logout());
        resetSessionExpiredPrompt();
        setDetails(null);
        navigate(loginPath, { replace: true });
    };

    return (
        <Dialog
            open={Boolean(details)}
            onClose={() => {}}
            className="fixed inset-0 z-[10000]"
        >
            <div className="fixed inset-0 bg-slate-950/55 backdrop-blur-md" aria-hidden="true" />
            <div className="fixed inset-0 flex items-center justify-center p-4">
                <Dialog.Panel className="relative w-full max-w-[460px] overflow-hidden rounded-[30px] border border-white/70 bg-white shadow-[0_32px_90px_rgba(15,23,42,0.28)]">
                    <div
                        className="absolute inset-x-0 top-0 h-28"
                        style={{
                            background: "linear-gradient(135deg, color-mix(in srgb, var(--color-primary) 12%, white), #fff 58%, color-mix(in srgb, var(--color-primary) 7%, white))",
                        }}
                    />
                    <div className="relative p-6 text-center sm:p-8">
                        <div
                            className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl text-white shadow-[0_14px_34px_color-mix(in_srgb,var(--color-primary)_25%,transparent)]"
                            style={{ background: "var(--color-primary)" }}
                        >
                            <ShieldAlert className="h-8 w-8" aria-hidden="true" />
                        </div>

                        <p className="mt-5 text-xs font-bold uppercase tracking-[0.28em] text-slate-400">
                            Session Expired
                        </p>

                        <Dialog.Title className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">
                            Please sign in again
                        </Dialog.Title>

                        <Dialog.Description className="mx-auto mt-3 max-w-sm text-sm leading-6 text-slate-600">
                            {details?.message || fallbackDetails.message}
                        </Dialog.Description>

                        <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-left text-sm leading-6 text-slate-600">
                            This happens when your secure login reaches its time limit or the server can no longer verify your account. Your screen is paused to protect the workspace.
                        </div>

                        <button
                            type="button"
                            onClick={handleBackToSignIn}
                            className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-4 text-sm font-semibold text-white shadow-[0_16px_34px_color-mix(in_srgb,var(--color-primary)_25%,transparent)] transition hover:brightness-95 focus:outline-none focus:ring-4"
                            style={{
                                background: "var(--color-primary)",
                                "--tw-ring-color": "color-mix(in srgb, var(--color-primary) 24%, transparent)",
                            }}
                        >
                            <LogIn className="h-5 w-5" aria-hidden="true" />
                            Back to Sign In
                        </button>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
};

export default SessionExpiredModal;
